import { gql, useMutation } from '@apollo/client';
import { AffiliationTypes } from '@generated/graphql';
import type { Account } from '@interfaces/account';
import { FlagsmithFlags } from '@utils/flagsmith';
import { useToast } from 'hephaestus/web';
import { useTranslation } from 'react-i18next';
import { useFeatureFlag } from '../../../../../hooks/useFeatureFlag';

export const UPDATE_ACCOUNT = gql`
  mutation updateAccount($input: UpdateAccountInput!) {
    updateAccount(input: $input) {
      account {
        id
        isTransferredFromAnotherInstitution
      }
    }
  }
`;

const isUnder21 = (dateOfBirthString?: string | null): boolean => {
  // Unknown DOB: don't block — let the backend validation be the source of truth.
  if (!dateOfBirthString) return true;
  const birth = new Date(dateOfBirthString);
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const monthDiff = now.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birth.getDate())) {
    age -= 1;
  }
  return age < 21;
};

const hasOverageRespBeneficiary = (account: Account): boolean =>
  (account.affiliations ?? []).some(
    (affiliation) =>
      (affiliation.type === AffiliationTypes.PrimaryBeneficiary ||
        affiliation.type === AffiliationTypes.ContingentBeneficiary) &&
      !isUnder21(affiliation.user?.dateOfBirth),
  );

/**
 * Keeps a reused (in-progress) RESP account's `isTransferredFromAnotherInstitution` flag
 * in sync with the value the user picked in the account-selection dialog.
 *
 * RESP primary beneficiaries cannot be deleted, so when the flag is turned off and an
 * existing beneficiary is now too old (21+), we keep the affiliation, update the flag, and
 * ask the user to update the beneficiary's date of birth on the next step (the backend
 * blocks any further affiliation change until it's fixed).
 *
 * Shared by the `createAccount` and `createAccountForGoal` sub-steps.
 *
 * Returns `true` when it actually persisted a change so callers (which fetch accounts with
 * `no-cache`) can refetch and avoid acting on a stale `isTransferredFromAnotherInstitution`.
 */
export const useRespTransferSync = () => {
  const { t } = useTranslation('workflowCompletions');
  const { enqueueToast } = useToast();
  const isRespInstitutionTransferEnabled = useFeatureFlag(
    FlagsmithFlags.resp_transferred_from_another_institution,
  );
  const [updateAccount] = useMutation(UPDATE_ACCOUNT);

  const syncRespTransferSelection = async (
    account: Account,
    newValue: boolean,
  ): Promise<boolean> => {
    if (!isRespInstitutionTransferEnabled) return false;
    if (!account?.type?.includes('RESP')) return false;

    const previousValue = account.isTransferredFromAnotherInstitution ?? false;
    if (previousValue === newValue) return false;

    await updateAccount({
      variables: {
        input: {
          accountId: account.id,
          isTransferredFromAnotherInstitution: newValue,
        },
      },
    });

    if (previousValue && !newValue && hasOverageRespBeneficiary(account)) {
      enqueueToast(t('respAccountCreation.updateBeneficiaryDobRequired'), {
        variant: 'warning',
      });
    }

    return true;
  };

  return { syncRespTransferSelection };
};
